import { useEffect, useRef, useState } from "react";
import { Play, X } from "lucide-react";
import { LeadForm } from "./LeadForm";
import { UnlockedPanel } from "./UnlockedPanel";
import { ArrowsNote } from "./ArrowsNote";
import { InkFilter } from "./Sketches";
import { track } from "./track";
import { viewer, useViewer } from "./viewerStore";
import { BOOTCAMP_URL, CHALLENGE_URL, reportIntent } from "./cta";

const VIDEO_ID = "pgjBuLpZAvU";
const PREVIEW_ID = "fwiHhCKaSW0";
const BAR_AFTER_SECONDS = 120;

/* Public teaser: YouTube player so the first real play can be counted. */
const PreviewPlayer = () => {
  const el = useRef(null);
  useEffect(() => {
    let alive = true;
    let p = null;
    loadYT().then((YT) => {
      if (!alive || !el.current) return;
      p = new YT.Player(el.current, {
        videoId: PREVIEW_ID,
        playerVars: { playsinline: 1, rel: 0, modestbranding: 1 },
        events: { onStateChange: (e) => { if (e.data === YT.PlayerState.PLAYING) track("teaser_play"); } },
      });
    });
    return () => { alive = false; p?.destroy?.(); };
  }, []);
  return <div className="hv-player"><div ref={el} /></div>;
};

const loadYT = () =>
  new Promise((resolve) => {
    if (window.YT?.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
    if (!document.getElementById("yt-api")) {
      const s = document.createElement("script");
      s.id = "yt-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    }
  });

const Cover = ({ onPlay }) => (
  <button type="button" className="video-cover" onClick={onPlay} aria-label="Play the free video" data-testid="video-cover-play-button">
    <img src="/img/video-cover.webp" alt="" />
    <span className="video-cover-shade" />
    <span className="vc-badge" data-testid="video-badge">FREE • 60 MIN</span>
    <span className="vc-lines">
      <span>ONE WTF IDEA.</span><span>ONE FUNNEL.</span><span className="gold-text">THE EXACT PATH.</span>
    </span>
    <span className="video-cover-play"><Play size={30} fill="currentColor" /></span>
  </button>
);

const ChallengeBar = ({ onClose }) => (
  <div className="cbar" data-testid="mobile-challenge-bar">
    <a href={BOOTCAMP_URL} onClick={() => reportIntent("bootcamp")} className="cbar-link" data-testid="mobile-challenge-bar-link">
      <span>DIY Bootcamp</span>
    </a>
    <a href={CHALLENGE_URL} onClick={() => reportIntent("dasha")} className="cbar-link dark" data-testid="mobile-challenge-bar-dasha">
      <span>With Dasha</span>
    </a>
    <button type="button" className="cbar-x" onClick={onClose} aria-label="Close" data-testid="mobile-challenge-bar-close"><X size={16} /></button>
  </div>
);

export const FreeVideo = () => {
  const { email, name, played } = useViewer();
  const unlocked = Boolean(email);
  const [returning] = useState(() => Boolean(email && name));
  const wrap = useRef(null);
  const formRef = useRef(null);
  const host = useRef(null);
  const player = useRef(null);
  const pending = useRef(false);
  const me = useRef({ email, played });
  me.current = { email, played };
  const [cover, setCover] = useState(true);
  const [success, setSuccess] = useState(false);
  const [liveSession, setLiveSession] = useState(false);
  const [bar, setBar] = useState(false);
  const [closed, setClosed] = useState(false);
  const showBar = bar && !closed;

  useEffect(() => {
    if (!liveSession || bar) return undefined;
    const t = setInterval(() => {
      if ((player.current?.getCurrentTime?.() ?? 0) >= BAR_AFTER_SECONDS) setBar(true);
    }, 1000);
    return () => clearInterval(t);
  }, [liveSession, bar]);

  useEffect(() => {
    document.body.classList.toggle("has-cbar", showBar);
    return () => document.body.classList.remove("has-cbar");
  }, [showBar]);

  useEffect(() => {
    if (!unlocked) return undefined;
    let alive = true;
    loadYT().then((YT) => {
      if (!alive || !host.current) return;
      player.current = new YT.Player(host.current, {
        videoId: VIDEO_ID,
        playerVars: { playsinline: 1, rel: 0, modestbranding: 1 },
        events: {
          onReady: (e) => { if (pending.current) e.target.playVideo(); },
          onStateChange: (e) => {
            if (e.data !== YT.PlayerState.PLAYING) return;
            track("class_play");
            setCover(false);
            setLiveSession(true);
            viewer.setPlayed();
          },
        },
      });
    });
    return () => { alive = false; player.current?.destroy?.(); player.current = null; };
  }, [unlocked]);

  const play = () => {
    pending.current = true;
    player.current?.playVideo?.();
  };

  const focusForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => formRef.current?.querySelector("input")?.focus({ preventScroll: true }), 450);
  };

  const start = () => (me.current.email ? play() : focusForm());

  useEffect(() => {
    const onRequest = () => {
      if (!me.current.email) { focusForm(); return; }
      wrap.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      play();
    };
    window.addEventListener("nbs:play", onRequest);
    return () => window.removeEventListener("nbs:play", onRequest);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onUnlocked = (mail, first) => {
    viewer.setLead(mail, first);
    track("signup");
    setSuccess(true);
    setTimeout(() => wrap.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const watchNow = () => {
    document.querySelector("[data-testid=masterclass-video]")?.scrollIntoView({ behavior: "smooth", block: "center" });
    play();
  };

  if (!unlocked) {
    return (
      <div className="hv gate" ref={wrap} data-testid="video-gate">
        <InkFilter />
        <div className="video-frame gate-preview" data-testid="preview-video">
          <PreviewPlayer />
        </div>
        <ArrowsNote />
        <div className="gate-form" ref={formRef}><LeadForm onUnlocked={onUnlocked} /></div>
      </div>
    );
  }

  return (
    <div className="hv" ref={wrap}>
      {success ? <UnlockedPanel onWatch={watchNow} /> : returning && <p className="hv-welcome" data-testid="welcome-back-note">Welcome back, {name}. Your free video is right here.</p>}
      <div className="video-frame" data-testid="masterclass-video">
        <div ref={host} className="hv-player" />
        {cover && <Cover onPlay={start} />}
      </div>
      {showBar && <ChallengeBar onClose={() => setClosed(true)} />}
    </div>
  );
};
